<template>
  <div class='zoomable-circle-packing-chart' id='chart-content'>
    <svg id='chart-content-svg' :width='width' :height='height'></svg>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  name: 'ZoomableCirclePackingChart',
  props: {
    data: {
      type: Object,
      required: true,
    },
    width: {
      default: 500,
      type: Number,
    },
    height: {
      default: 500,
      type: Number,
    },
    update: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      focus: {},
      margin: 20,
      view: {},
      diameter: this.width,
      circle: {},
      node: {},
      labelHover: {},
    };
  },
  mounted() {
    this.buildChart();
  },
  computed: {
  },
  methods: {
    buildChart() {
      if (this.data.name === undefined) {
        return;
      }
      const svg = d3.select('#chart-content-svg');
      svg.selectAll('*').remove();
      const margin = 20;
      this.diameter = +this.width;
      const g = svg.append('g').attr('transform', `translate(${ this.diameter / 2 },${ this.diameter / 2 })`);

      const color = d3.scaleLinear()
        .domain([0, 5])
        .range(['hsl(1, 64%, 81%)', 'hsl(343, 38%, 51%)'])
        .interpolate(d3.interpolateHcl);

      const pack = d3.pack()
        .size([this.diameter - margin, this.diameter - margin])
        .padding(2);

      const root = d3.hierarchy(this.data)
        .sum(d => d.value)
        .sort((a, b) => b.value - a.value);
      this.focus = root;
      const nodes = pack(root).descendants();
      // eslint-disable-next-line no-unused-vars
      this.circle = g.selectAll('circle')
        .data(nodes)
        .enter().append('circle')
        // eslint-disable-next-line no-nested-ternary
        .attr('class', d => (d.parent ? d.children ? 'node' : 'node node--leaf' : 'node node--root'))
        .style('fill', d => {
          if (d.children) {
            return color(d.depth);
          }
          return d.data.covered ? 'rgba(36,190,181,0.9)' : null;
        })
        .on('click', (event, d) => {
          if (!d.children) {
            this.zoom(d.parent);
            event.stopPropagation();
            this.$emit('domainSelected', d.data.name);
          } else if (this.focus !== d) {
            this.zoom(d);
            event.stopPropagation();
          }
        });
      // eslint-disable-next-line no-unused-vars
      const rect = g.selectAll('rect')
        .data(nodes)
        .enter().filter(d => d.parent === root)
        .append('rect');

      // eslint-disable-next-line no-unused-vars
      const text = g.selectAll('text')
        .data(nodes)
        .enter().append('text')
        .attr('style', d => (d.parent === root ? `font: ${this.getLabelFontSize(d)}px "Inter-SemiBold", sans-serif;` : ''))
        .attr('class', d => (d.parent === root ? 'label' : 'label--leaf'))
        .attr('y', 6)
        .style('fill-opacity', d => (d.parent === root ? 1 : 0))
        .style('display', d => (d.parent === root ? 'inline' : 'none'))
        .text(d => d.data.name)
        .style('pointer-events', 'none')
        .attr('id', d => d.data.name.replace(/[\s.]/g, '-').toLowerCase())
        .each((d, i, svgNodes) => {
          // eslint-disable-next-line no-param-reassign
          d.width = svgNodes[i].getBBox().width + 8;
          // eslint-disable-next-line no-param-reassign
          d.height = svgNodes[i].getBBox().height;
        });

      rect.attr('width', d => d.width || 0)
        .attr('x', d => (d.width ? `-${d.width / 2}` : '0'))
        .attr('y', d => {
          if (!d.height) return '0';
          const yVal = d.r < 20 ? (d.height / 2) - 3 : d.height / 2;
          return yVal < 0 ? String(yVal) : `-${yVal}`;
        })
        .attr('height', d => d.height || 0)
        .attr('rx', 5)
        .attr('class', 'labels-background')
        .style('pointer-events', 'none')
        .attr('id', d => `rect-${d.data.name.replace(/[\s.]/g, '-').toLowerCase()}`);

      g.selectAll('circle')
        .on('mouseenter', (e, d) => {
          // Only show labels for leaf nodes (bubbles without children)
          if (d.parent && !d.children && d.parent === this.focus) {
            const label = d.data.name ? d.data.name.replace(/[\s.]/g, '-').toLowerCase() : '';
            if (label && this.labelHover.label !== label) {
              // Hide the previous label
              if (this.labelHover.label) {
                g.select(`#${this.labelHover.label}`)
                  .style('display', 'none')
                  .style('opacity', 0);
                g.select(`#rect-${this.labelHover.label}`)
                  .style('display', 'none');
              }
              // Show the current label
              this.labelHover.label = label;
              g.select(`#${label}`)
                .style('display', 'inline')
                .style('opacity', 1);
              g.select(`#rect-${label}`)
                .style('display', 'inline');
              this.labelTrack = true;
            }
          }
        })
        .on('mouseleave', (e, d) => {
          if (d.parent && !d.children && d.parent === this.focus) {
            const label = d.data.name ? d.data.name.replace(/[\s.]/g, '-').toLowerCase() : '';
            if (label) {
              g.select(`#${label}`)
                .style('display', 'none')
                .style('opacity', 0);
              g.select(`#rect-${label}`)
                .style('display', 'none');
            }
            this.labelHover.label = '';
          }
        });
      this.node = g.selectAll('circle,text,rect');
      const nodeRoot = g.select('.node--root');
      nodeRoot.on('mouseenter', () => {
        if (this.labelHover.label) {
          g.select(`#${this.labelHover.label}`)
            .style('display', 'inline');
          g.select(`#rect-${this.labelHover.label}`)
            .style('display', 'inline');
        }
      });
      svg
        .style('background', 'none')
        .on('click', () => {
          this.zoom(root);
          this.$emit('domainSelected', '');
        });

      this.zoomTo([root.x, root.y, root.r * 2 + margin]);
    },
    zoom(d) {
      this.$emit('countrySelected', d.data.name);
      // eslint-disable-next-line no-unused-vars
      const focus0 = this.focus;
      this.focus = d;
      const transition = d3.transition()
        // .duration(d3.event.altKey ? 7500 : 750)
        .duration(750)
        // eslint-disable-next-line no-unused-vars
        .tween('zoom', data => {
          const i = d3.interpolateZoom(this.view, [this.focus.x, this.focus.y, this.focus.r * 2 + this.margin]);
          return t => { this.zoomTo(i(t)); };
        });
      transition.selectAll('text')
        .filter((data, i, nodes) => data && (data.parent === this.focus || nodes[i].style.display === 'inline'))
        .style('fill-opacity', data => (data && data.parent === this.focus ? 1 : 0))
        .on('start', (data, i, nodes) => {
          if (data && data.parent === this.focus) {
            // eslint-disable-next-line no-param-reassign
            nodes[i].style.display = 'inline';
          }
        })
        .on('end', (data, i, nodes) => {
          if (data && data.parent !== this.focus) {
            // eslint-disable-next-line no-param-reassign
            nodes[i].style.display = 'none';
          }
        });
      transition.selectAll('rect')
        .on('start', (data, i, nodes) => {
          if (data && data.parent === this.focus) {
            // eslint-disable-next-line no-param-reassign
            nodes[i].style.display = 'inline';
          }
        })
        .on('end', (data, i, nodes) => {
          if (data && data.parent !== this.focus) {
            // eslint-disable-next-line no-param-reassign
            nodes[i].style.display = 'none';
          }
        });
    },
    zoomTo(v) {
      const k = this.diameter / v[2];
      this.view = v;
      this.node.attr('transform', d => `translate(${ (d.x - v[0]) * k },${ (d.y - v[1]) * k })`);
      this.circle.attr('r', d => d.r * k);
    },
    // eslint-disable-next-line no-unused-vars
    getLabelFontSize(d) {
      if (d.r < 5) {
        return 6;
      }
      if (d.r < 10) {
        return 8;
      }
      if (d.r < 20) {
        return 10;
      }
      if (d.r < 60) {
        return 15;
      }
      return 20;
    },
  },
  watch: {
    update() {
      if (this.update) {
        this.buildChart();
        this.$emit('chartUpdated');
      }
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';

.zoomable-circle-packing-chart {
  --chart-label-fill: #1a1e22;
  --chart-label-bg: #ffffff;
  --chart-label-bg-opacity: 0.85;

  margin-top: rem(-10);

  .labels-background {
    fill: var(--chart-label-bg);
    fill-opacity: var(--chart-label-bg-opacity);
    stroke: none;
    stroke-width: 0;
  }

  .node {
    cursor: pointer;

    &:hover {
      stroke: var(--brand-primary);
      stroke-width: 2px;
    }

    &:active {
      stroke: var(--brand-secondary);
      stroke-width: 2px;
    }
  }

  .node--leaf {
    fill: #ffffff;

    &:hover {
      stroke: var(--brand-primary);
      stroke-width: 2px;
    }

    &:active {
      stroke: var(--brand-secondary);
      stroke-width: 2px;
    }
  }

  .label,
  .label--leaf {
    font: rem(20) $font-family-semibold;
    text-anchor: middle;
    text-transform: uppercase;
    padding-top: $space-1;
  }

  // Country labels
  .label {
    fill: var(--chart-label-fill);
    pointer-events: none;
  }

  // Domain labels on hover
  .label--leaf {
    font: $font-size-sm $font-family-medium;
    fill: var(--chart-label-fill);
    opacity: 0;
    user-select: none;

    &:hover {
      opacity: 1;
    }
  }

  .node--root {
    pointer-events: all;

    &:hover {
      stroke: none !important;
    }
  }
}

[data-theme="dark"] .zoomable-circle-packing-chart {
  --chart-label-fill: #ffffff;
  --chart-label-bg: #1a1e22;
  --chart-label-bg-opacity: 0.85;
}
</style>
